import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { Crumb } from 'src/app/common/bread-crumbs/crumb';
import { AlertService } from 'src/app/core/alerts/alerts.service';
import { GIQError } from 'src/app/core/errors/giq-error';
import { Report } from 'src/app/models/reports';
import { ReportsService } from '../../core/reports.service';
import { userInfo$ } from 'src/app/core/state/user/user.store';
import { UserInfo } from 'src/app/core/state/user/user-info';
import * as QuicksightEmbedding from 'amazon-quicksight-embedding-sdk';
import { AccessService } from 'src/app/common/access/access.service';
declare var tableau: any;

@Component({
  selector: 'giq-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  standalone: false,
})
export class ReportComponent implements OnDestroy {
  public viz: any;
  public crumbs: Crumb[] = [];
  public reportInfo$: Observable<Report | undefined>;
  public error$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public errorMessage: string = '';
  public customWidth = false;
  public reportInfo: Report | undefined = undefined;
  public reportId: string;
  public reportDescription: string | undefined;
  public compact: boolean | undefined = false;
  public subReport: string | null = null;
  public hint: string | null = null;
  public loading: boolean = false;

  private subReportInfo: Report | undefined = undefined;

  constructor(
    private activatedRoute: ActivatedRoute,
    private reportsService: ReportsService,
    private alertService: AlertService,
    private accessService: AccessService,
    private router: Router
  ) {
    this.reportId = activatedRoute.snapshot.params['reportId'];
    this.reportInfo$ = combineLatest([
      activatedRoute.queryParams,
      activatedRoute.params,
    ]).pipe(
      map(([query, params]: [Params, Params]) => {
        this.resetViz();
        this.loading = true;
        this.subReport = query['subReport'] || null;
        this.hint = query['hint'] || null;
        console.log('subreport', this.subReport);
        console.log('hint', this.hint);
        return params['reportId'];
      }),
      filter((x) => x != null && x !== '' && x !== 'null'),
      switchMap((reportId: string) => {
        return reportsService.getReport(reportId);
      }),
      withLatestFrom(userInfo$),
      switchMap(
        ([reportInfo, userInfo]: [
          Report | undefined,
          UserInfo | undefined
        ]): Observable<
          [Report | undefined, UserInfo | undefined, Report | undefined]
        > => {
          if (reportInfo?.qnA == null) {
            this.subReport = null;
          }
          if (this.subReport != null) {
            return this.reportsService
              .getReport(this.subReport)
              .pipe(map((x) => [reportInfo, userInfo, x]));
          } else {
            return of([reportInfo, userInfo, undefined]);
          }
        }
      ),
      switchMap(
        ([reportInfo, userInfo, subReportInfo]: [
          Report | undefined,
          UserInfo | undefined,
          Report | undefined
        ]): Observable<Report | undefined> => {
          this.reportInfo = reportInfo;
          this.subReportInfo = subReportInfo;
          if (reportInfo == null) {
            this.error$.next(true);
            this.errorMessage = 'Error loading report information.';
            this.reportDescription = 'Error loading report information';
            return of(undefined);
          } else {
            this.compact = reportInfo.compact;
            this.reportDescription =
              reportInfo?.categoryName + '/' + reportInfo.name;
            this.crumbs = [
              { name: 'Reports', url: '/home' },
              {
                name: reportInfo?.categoryName ?? '',
                url: '/reports',
                query: { category: reportInfo.categoryId },
              },
            ];
            if (this.subReport != null) {
              this.crumbs.push({
                name: reportInfo?.name ?? '',
                url: '/reports/' + reportInfo.id,
              });
              this.crumbs.push({
                name: 'QnA AI',
                url: undefined,
              });
            }
            if (subReportInfo != null) {
              return this.renderReport(subReportInfo, userInfo);
            } else {
              return this.renderReport(reportInfo, userInfo);
            }
          }
        }
      ),
      catchError((err: GIQError) => {
        this.loading = false;
        this.error$.next(true);
        this.errorMessage = err.errorMessage;
        return throwError(() => err);
      })
    );
  }

  ngOnDestroy(): void {
    this.resetViz();
  }

  private renderReport(
    reportInfo: Report,
    userInfo: UserInfo | undefined
  ): Observable<Report | undefined> {
    if (reportInfo.url != null) {
      if (userInfo?.userName != null) {
        reportInfo.url = reportInfo.url.replace(
          '{username}',
          userInfo?.userName
        );
      } else {
        console.error('Got invalid user information');
      }
      if (reportInfo?.dataSourceId == 'TABLEAU') {
        setTimeout(() => {
          this.initTableau(reportInfo);
        }, 200);
        return of(reportInfo);
      } else {
        if (reportInfo.needsDynamicUrl === true) {
          return this.reportsService.GetDynamicUrl(reportInfo.id).pipe(
            map((x) => {
              reportInfo.url = x.url;
              setTimeout(() => {
                this.initComponent(reportInfo);
              }, 200);
              return reportInfo;
            }),
            catchError((err: GIQError) => {
              this.alertService.error(err.errorMessage);
              this.error$.next(true);
              this.errorMessage = err.errorMessage;
              this.reportDescription = 'Error loading report information';
              return of(undefined);
            })
          );
        } else {
          setTimeout(() => {
            this.initComponent(reportInfo);
          }, 200);
          return of(reportInfo);
        }
      }
    } else {
      return throwError(() => {
        errorMessage: 'Incorrect report configuration';
      });
    }
  }

  private initComponent(reportInfo: Report) {
    if (reportInfo?.dataSourceId == 'QUICKSIGHT') {
      this.initQuickSightNow(reportInfo);
    } else {
      this.initIFrameNow(reportInfo);
    }
  }

  public openQnA(): void {
    if (this.reportInfo?.qnA != null) {
      this.router.navigate([], {
        queryParams: { subReport: 'qna-ai', hint: this.reportInfo?.qnA },
        relativeTo: this.activatedRoute,
      });
    }
  }

  initIFrameNow(reportInfo: Report) {
    this.loading = false;
    this.resetViz();
    const containerDiv = document.getElementById('vizContainer');
    const iframe = document.createElement('iframe');
    iframe.src = reportInfo.url ?? '';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    containerDiv?.appendChild(iframe);
  }

  private resetViz() {
    if (this.viz) {
      this.viz.dispose();
      this.viz = null;
    }
    const containerDiv = document.getElementById('vizContainer');
    if (containerDiv?.firstChild != null) {
      containerDiv?.removeChild(containerDiv.firstChild);
    }
  }

  initTableau(reportInfo: Report) {
    this.loading = false;
    this.resetViz();
    let containerDiv = document.getElementById('vizContainer');
    const options: any = {
      hideTabs: reportInfo.showTabs != null ? !reportInfo.showTabs : true,
      hideToolbar:
        reportInfo.showToolbar != null ? !reportInfo.showToolbar : true,
      device: 'desktop',
      onFirstInteractive: () => {
        this.accessService.access(this.reportId, 'report_access');
      },
    };

    if (reportInfo.width != null) {
      options.width = reportInfo.width;
      this.customWidth = true;
    }

    if (reportInfo.height != null) {
      options.height = reportInfo.height;
    }

    this.viz = new tableau.Viz(containerDiv!, reportInfo.url, options);
  }

  initQuickSightNow(reportinfo: Report) {
    this.loading = false;
    this.resetViz();
    let containerDiv = document.getElementById('vizContainer');
    const frameOptions: QuicksightEmbedding.FrameOptions = {
      url: reportinfo.url || '',
      container: containerDiv || 'vizContainer',
      height: '100%',
      width: '100%',
      resizeHeightOnSizeChangedEvent: true,
      className: 'quicksight-frame',
    };
    const contentOptions: QuicksightEmbedding.ContentOptions = {
      attributionOptions: {
        overlayContent: true,
      },
      panelOptions: {
        showQIcon: false,
        title: 'Talk to Your Data. Get Instant Answers.',
        panelType: 'FULL',
      },
      allowFullscreen: true,
      allowTopicSelection: true,
      showPinboard: true,
    };
    from(QuicksightEmbedding.createEmbeddingContext()).subscribe(
      (embeddingContext: any) => {
        const dashboard = embeddingContext.embedGenerativeQnA(
          frameOptions,
          contentOptions
        );
      }
    );
  }
}
